import { useRef, useEffect, useCallback, useState } from 'react'

interface WebcamProps {
  onCapture?: (imageBase64: string) => void
  onStream?: (stream: MediaStream) => void
  showCapture?: boolean
  className?: string
  mirrored?: boolean
}

export default function Webcam({
  onCapture,
  onStream,
  showCapture = true,
  className = '',
  mirrored = true,
}: WebcamProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Initialize webcam
  useEffect(() => {
    let stream: MediaStream | null = null
    let mounted = true

    const initCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user',
          },
        })

        if (videoRef.current && mounted) {
          videoRef.current.srcObject = stream

          // Wait for video to be ready to play
          videoRef.current.onloadedmetadata = () => {
            if (videoRef.current && mounted) {
              videoRef.current.play()
                .then(() => {
                  if (mounted) {
                    setIsReady(true)
                    onStream?.(stream!)
                  }
                })
                .catch(() => {
                  // On mobile, autoplay might be blocked - still show the video
                  if (mounted) {
                    setIsReady(true)
                    onStream?.(stream!)
                  }
                })
            }
          }
        }
      } catch (err) {
        void err
        if (mounted) {
          setError('Unable to access camera. Please grant permission and reload.')
        }
      }
    }

    initCamera()

    return () => {
      mounted = false
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [onStream])

  // Capture current frame
  const captureFrame = useCallback((): string | null => {
    if (!videoRef.current || !canvasRef.current || !isReady) return null

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    if (!ctx) return null

    // Set canvas size to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw video frame (flip horizontally if mirrored)
    if (mirrored) {
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
    }
    ctx.drawImage(video, 0, 0)

    // Convert to base64 (JPEG for smaller size)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
    const base64 = dataUrl.split(',')[1]

    return base64
  }, [isReady, mirrored])

  // Handle capture button click
  const handleCapture = useCallback(() => {
    const base64 = captureFrame()
    if (base64) {
      onCapture?.(base64)
    }
  }, [captureFrame, onCapture])

  // Expose capture method via ref
  useEffect(() => {
    (window as any).webcamCapture = captureFrame
    return () => {
      delete (window as any).webcamCapture
    }
  }, [captureFrame])

  if (error) {
    return (
      <div className={`bg-assess-card rounded-2xl flex items-center justify-center ${className}`}>
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-400 mb-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-primary-400 hover:text-primary-300 text-sm"
          >
            Reload page
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {/* Loading state */}
      {!isReady && (
        <div className="absolute inset-0 bg-assess-card rounded-2xl flex items-center justify-center z-10">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Initializing camera...</p>
          </div>
        </div>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`w-full h-full object-cover rounded-2xl ${mirrored ? 'scale-x-[-1]' : ''}`}
      />

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Capture button */}
      {showCapture && isReady && (
        <button
          onClick={handleCapture}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-primary-500 hover:bg-primary-600 px-6 py-3 rounded-full text-white font-medium flex items-center gap-2 transition-all hover:scale-105 shadow-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Capture Photo
        </button>
      )}

      {/* Face guide overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="w-full h-full" viewBox="0 0 640 480">
          <ellipse
            cx="320"
            cy="220"
            rx="120"
            ry="160"
            fill="none"
            stroke="rgba(14, 165, 233, 0.5)"
            strokeWidth="2"
            strokeDasharray="10 5"
          />
          <text x="320" y="420" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="14">
            Position your face within the oval
          </text>
        </svg>
      </div>
    </div>
  )
}

// Hook to capture webcam frame from other components
export function useWebcamCapture() {
  const capture = useCallback((): string | null => {
    if ((window as any).webcamCapture) {
      return (window as any).webcamCapture()
    }
    return null
  }, [])

  return { capture }
}
